import { Modal } from '@/components/Layout';
import { Divider } from 'primereact/divider';
import { useEstablishmentStore } from '@/store/EstablishmentStore/EstablishmentStore';
import {
  useGetVerifiedSchools,
  useGetClassCountBySchool,
  useGetSchoolLessonsWithProgressions,
} from '@/api/hooks';
import { getEstablishmentTypeLabel, getStatusLabel } from '../utils/helpers';
import iconCall from '@/assets/icons/call.svg';
import iconLocation from '@/assets/icons/location.svg';
import iconEmail from '@/assets/icons/email.svg';
import iconSchool from '@/assets/icons/schoolStats.svg';
import iconStatus from '@/assets/icons/status.svg';
import iconPerson from '@/assets/icons/person.svg';
import iconTeacher from '@/assets/icons/teacher.svg';
import iconStudent from '@/assets/icons/students.svg';
import iconCredit from '@/assets/icons/credit.svg';
import iconClasses from '@/assets/icons/bxs_school.svg';
import iconLessons from '@/assets/icons/module.svg';

type StatCardProps = {
  icon: string;
  label: string;
  value: number | string;
};

const StatCard = ({ icon, label, value }: StatCardProps): JSX.Element => (
  <div className="bg-grayishBlack p-5 rounded-lg w-[133px] mr-2.5">
    <div className="h-[44.86px] flex justify-center items-center bg-khakibrown w-[44.86px] rounded-md">
      <img src={icon} alt={label.toLowerCase()} className="w-5 h-5" />
    </div>
    <Divider
      layout="horizontal"
      className="w-[61.69px] h-[2.24px] bg-primary border border-solid border-primary mt-[27px] mb-0"
    />
    <div className="">
      <span className="text-sm text-midGrey font-normal block mt-[13px]">
        {label}
      </span>
      <span className="text-[35.89px] font-semibold">{value}</span>
    </div>
  </div>
);

interface InfoRowProps {
  icon: string;
  label: string;
  value: string;
  containerClassName?: string;
  iconClassName?: string;
  labelClassName?: string;
  valueClassName?: string;
}

const InfoRow = ({
  icon,
  label,
  value,
  containerClassName = 'flex items-center space-x-2 pb-4',
  iconClassName = 'w-5 h-5 mr-4',
  labelClassName = 'text-sm font-normal text-mediumGrey text-[15px]',
  valueClassName = 'text-sm font-normal text-[15px]',
}: InfoRowProps): JSX.Element => (
  <div className={containerClassName}>
    <img src={icon} alt={label.toLowerCase()} className={iconClassName} />
    <div>
      <p className={labelClassName}>{label}</p>
      <span className={valueClassName}>{value}</span>
    </div>
  </div>
);

export const EstablishmentDetail: React.FC = (): JSX.Element => {
  const {
    isOpenEstablishmentDetailModal,
    closeEstablishmentDetailModal,
    selectedEstablishmentId,
  } = useEstablishmentStore();

  // Récupérer les données des établissements
  const { data: establishments } = useGetVerifiedSchools({});

  // Trouver l'établissement sélectionné
  const establishment = establishments?.find(
    (est) => est._id === selectedEstablishmentId,
  );

  const { data: classCount, isLoading: isLoadingClassCount } =
    useGetClassCountBySchool(selectedEstablishmentId);
  const { data: lessons, isLoading: isLoadingLessons } =
    useGetSchoolLessonsWithProgressions(selectedEstablishmentId);

  // Si pas d'établissement trouvé, ne rien afficher
  if (!establishment) {
    return (
      <Modal
        isOpen={isOpenEstablishmentDetailModal}
        onClose={closeEstablishmentDetailModal}
        modalStyle="bg-carbon w-[637px] h-[558px]"
        headerStyle="bg-carbon text-white"
      >
        <div className="text-white font-raleway flex items-center justify-center h-full">
          <p>Établissement non trouvé</p>
        </div>
      </Modal>
    );
  }

  const STATS: StatCardProps[] = [
    {
      icon: iconTeacher,
      label: 'Professeurs',
      value: isLoadingClassCount ? '...' : (classCount?.teachersCount ?? 0),
    },
    {
      icon: iconClasses,
      label: 'Classes',
      value: isLoadingClassCount ? '...' : (classCount?.classesCount ?? 0),
    },
    {
      icon: iconStudent,
      label: 'Élèves',
      value: isLoadingClassCount ? '...' : (classCount?.studentsCount ?? 0),
    },
    {
      icon: iconLessons,
      label: 'Leçons',
      value: isLoadingClassCount ? '...' : (classCount?.lessonsCount ?? 0),
    },
    {
      icon: iconCredit,
      label: 'Crédit AI',
      value: establishment?.iaCredit ?? 0,
    },
  ];

  return (
    <Modal
      isOpen={isOpenEstablishmentDetailModal}
      onClose={closeEstablishmentDetailModal}
      modalStyle="bg-carbon w-[760px] max-h-[80vh] overflow-y-auto"
      headerStyle="bg-carbon text-white"
    >
      <div className="text-white font-raleway">
        <div className="flex flex-row">
          {/* Colonne gauche - Informations textuelles */}
          <div className="w-1/2 space-y-4 pr-8">
            <h2 className="text-xl font-bold text-white">
              {establishment.name}
            </h2>

            {/* Informations de l'établissement */}
            <div className="space-y-3 pt-4">
              <InfoRow
                icon={iconStatus}
                label="Statut"
                value={getStatusLabel(establishment.status || '')}
                valueClassName={
                  establishment.status === 'ACTIVE'
                    ? 'text-green-500'
                    : 'text-yellow-500'
                }
              />
              <InfoRow
                icon={iconPerson}
                label="Responsable"
                value={
                  `${establishment.contact?.firstName || ''} ${establishment.contact?.lastName || ''}`.trim() ||
                  'Non renseigné'
                }
              />
              <InfoRow
                icon={iconEmail}
                label="Email Contact"
                value={establishment.contact?.email || 'Non renseigné'}
              />
              <InfoRow
                icon={iconSchool}
                label="Type"
                value={getEstablishmentTypeLabel(establishment.type || '')}
              />
              <InfoRow
                icon={iconLocation}
                label="Adresse"
                value={establishment.address || 'Non renseignée'}
              />
              <InfoRow
                icon={iconCall}
                label="Téléphone"
                value={establishment.contact?.phone || 'Non renseigné'}
              />
            </div>
          </div>

          {/* Colonne droite - Grille des statistiques */}
          <div className="w-1/2 pl-8 mt-16">
            <div className="grid grid-cols-2 gap-4">
              {STATS.map((stat, index) => (
                <StatCard key={index} {...stat} />
              ))}
            </div>
          </div>
        </div>

        {/* Leçons et progressions créées */}
        <div className="mt-8 pt-6 border-t border-white/10">
          <h3 className="text-sm font-semibold text-white mb-3">
            Leçons {lessons?.length ? `(${lessons.length})` : ''}
          </h3>
          {isLoadingLessons && (
            <p className="text-sm text-midGrey">Chargement des leçons...</p>
          )}
          {!isLoadingLessons && !lessons?.length && (
            <p className="text-sm text-midGrey">Aucune leçon</p>
          )}
          {!isLoadingLessons && !!lessons?.length && (
            <div className="max-h-[180px] overflow-y-auto pr-2 space-y-2">
              {lessons.map((lesson) => (
                <div
                  key={lesson._id}
                  className="flex items-center justify-between bg-grayishBlack rounded-md px-3 py-2"
                >
                  <span className="text-sm text-white truncate pr-4">
                    {lesson.name}
                  </span>
                  <span className="text-xs text-midGrey whitespace-nowrap">
                    {lesson.progressionsCount ?? 0} progression
                    {(lesson.progressionsCount ?? 0) > 1 ? 's' : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
